import { Decimal } from 'decimal.js';
/** D365 legal entities on this tenant use INR; every other currency this app
 * has seen so far reads with Western (thousands) grouping. */
const INDIAN_GROUPING_CURRENCY = 'INR';
function isDecimalLike(value) {
    return typeof value === 'object' && value !== null && typeof value.toFixed === 'function';
}
/** Decimal-safe: never routes the value through a JS float, even for a plain string/number input. */
function toFixedDecimalString(value, decimalPlaces) {
    if (isDecimalLike(value))
        return value.toFixed(decimalPlaces);
    // Called without `new` — decimal.js's own factory-function form (its .d.ts declares
    // `Decimal` as both a class and a callable function); under this package's NodeNext
    // resolution `new Decimal(...)` fails TS7's construct-signature check on the merged
    // class/function declaration decimal.js ships, even though the class form works fine
    // under apps/web's `bundler` resolution (see `apps/web/src/lib/totals.ts`). The two
    // forms are equivalent at runtime — this just sidesteps the resolution-mode-specific
    // typecheck gap rather than fighting it.
    return Decimal(value).toFixed(decimalPlaces);
}
/** Western (thousands) grouping: `1234567` → `1,234,567`. */
function groupWestern(digits) {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
/**
 * Indian numbering system: the last three digits stand alone, then every
 * remaining pair from the right gets its own comma — `1240000` → `12,40,000`,
 * `123456789` → `12,34,56,789`.
 */
function groupIndian(digits) {
    if (digits.length <= 3)
        return digits;
    const lastThree = digits.slice(-3);
    const rest = digits.slice(0, -3);
    const groupedRest = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    return `${groupedRest},${lastThree}`;
}
function formatWithGrouping(value, currencyCode, decimalPlaces) {
    const fixed = toFixedDecimalString(value, decimalPlaces);
    const negative = fixed.startsWith('-');
    const unsigned = negative ? fixed.slice(1) : fixed;
    const [integerPart, fractionPart] = unsigned.split('.');
    const grouped = currencyCode === INDIAN_GROUPING_CURRENCY
        ? groupIndian(integerPart ?? '0')
        : groupWestern(integerPart ?? '0');
    const sign = negative ? '-' : '';
    return fractionPart ? `${sign}${grouped}.${fractionPart}` : `${sign}${grouped}`;
}
/**
 * A money amount to two decimal places, grouped for its currency — Indian
 * grouping for INR, Western grouping for everything else (X1).
 */
export function formatMoney(value, currencyCode) {
    return formatWithGrouping(value, currencyCode, 2);
}
/** Totals (line total, grand total) — X1's "totals: 2 decimals". An alias of
 * `formatMoney` kept as its own name because a total and a unit price round
 * to different precisions, and a call site should say which one it means. */
export const formatTotal = formatMoney;
/** A line's net (unit) price — X1's "net price: 4 decimals". */
export function formatNetPrice(value, currencyCode) {
    return formatWithGrouping(value, currencyCode, 4);
}
function toDate(input) {
    return input instanceof Date ? input : new Date(input);
}
function formatWithIntl(input, options) {
    const date = toDate(input);
    if (Number.isNaN(date.getTime()))
        return '';
    return new Intl.DateTimeFormat('en-GB', options).format(date);
}
/**
 * A date with no ambiguity between `MM/DD` and `DD/MM` — `12 Aug 2026`, the
 * `formatReviewDate` pattern this module replaces. Used wherever only the
 * date, not the time, matters (a delivery date, a term date, "last edited").
 */
export function formatDate(input, options = {}) {
    return formatWithIntl(input, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        ...options,
    });
}
/**
 * A date and time, unambiguous on both halves — `12 Aug 2026, 14:35`
 * (24-hour). Used for real instants a person needs to place in time: audit
 * history, an admin audit-log row, a rejection notice.
 */
export function formatDateTime(input, options = {}) {
    return formatWithIntl(input, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        ...options,
    });
}
/** Time of day only, 24-hour — "Draft saved 14:32" (wireframe R7). */
export function formatTime(input, options = {}) {
    return formatWithIntl(input, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        ...options,
    });
}
